import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useQuery, useMutation, useQueryClient } from "react-query";
import useApiService from "../../utilities/useApiService";
import { EditTemporaryNdcModal } from "./EditTemporaryNdcModal";
import TableOrganism from "../Table/TableOrganism";
import { createTemporaryNdcRows } from "./CreateTemporaryNdcRows";
import { createHeaderRow } from "../../utilities/CreateHeaderRow";
import { useTemporaryNdcStyles } from "./TemporaryNdcStyles";
import {
  filterPendingTempDrugsForReturnItems,
  TEMP_NDCS_QUERY_KEY,
} from "../../utilities/ndc/temporaryNdcUtils";
import AreYouSure from "../../molecules/AreYouSure/AreYouSure";
import { toastFail } from "../../utilities/toast";
import { getApiErrorMessageOrFallback } from "../../utilities/apiErrors";

interface Props {
  open: boolean;
  onClose: () => void;
  returnItems?: Array<{ ndc: string }>;
}

export interface TempDrug {
  id: number;
  ndc: string;
  blockingNdc?: string;
  drugName: string;
  strength: string;
  strengthUom?: string;
  dosageForm: string;
  manufacturer: string;
  packageSize?: number;
  packageQuantity?: number;
  controlledSubstanceCode?: string;
  packagePrice?: number;
  unitPrice?: number;
  packageActualPrice?: number;
  unitActualPrice?: number;
  existsInMedispan: boolean;
}

type LoadingAction = {
  type: "accept" | "replace" | "delete";
  id: number;
};

const TemporaryNdcModal: React.FC<Props> = ({ open, onClose, returnItems }) => {
  const classes = useTemporaryNdcStyles();
  const { getMethod, postMethod, deleteMethod } = useApiService();
  const queryClient = useQueryClient();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedTempDrug, setSelectedTempDrug] = useState<any>(null);
  const [loadingAction, setLoadingAction] = useState<LoadingAction | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const isReturnScoped = returnItems != null;

  const getTempNdcs = async () => {
    const res = await getMethod("/temporary-ndcs");
    return res.data as TempDrug[];
  };

  const { data: allPending = [], isLoading } = useQuery(
    TEMP_NDCS_QUERY_KEY,
    getTempNdcs,
    {
      enabled: open,
    },
  );

  const displayData = isReturnScoped
    ? filterPendingTempDrugsForReturnItems(allPending, returnItems)
    : allPending;

  const invalidateTempNdcs = async () => {
    await queryClient.invalidateQueries(TEMP_NDCS_QUERY_KEY);
    await queryClient.invalidateQueries("items");
  };

  const acceptMutation = useMutation(
    async (id: number) => {
      setLoadingAction({ type: "accept", id });
      await postMethod(`/temporary-ndcs/${id}/accept`, {});
    },
    {
      onSuccess: async () => {
        setLoadingAction(null);
        await invalidateTempNdcs();
      },
      onError: (error) => {
        setLoadingAction(null);
        toastFail(
          getApiErrorMessageOrFallback(error, "Failed to accept temporary NDC."),
        );
      },
    },
  );

  const replaceMutation = useMutation(
    async (id: number) => {
      setLoadingAction({ type: "replace", id });
      await postMethod(`/temporary-ndcs/${id}/replace`, {});
    },
    {
      onSuccess: async () => {
        setLoadingAction(null);
        await invalidateTempNdcs();
      },
      onError: (error) => {
        setLoadingAction(null);
        toastFail(
          getApiErrorMessageOrFallback(error, "Failed to replace temporary NDC."),
        );
      },
    },
  );

  const deleteMutation = useMutation(
    async (id: number) => {
      setLoadingAction({ type: "delete", id });
      await deleteMethod(`/temporary-ndcs/${id}`);
    },
    {
      onSuccess: async () => {
        setLoadingAction(null);
        await invalidateTempNdcs();
      },
      onError: (error) => {
        setLoadingAction(null);
        toastFail(
          getApiErrorMessageOrFallback(error, "Failed to remove temporary NDC."),
        );
      },
    },
  );

  const header = createHeaderRow([
    "NDC",
    "Description",
    "Strength",
    "Dosage",
    "Pkg Size",
    "Pkg Qty",
    "Manufacturer",
    "CSC",
    "Pkg Price",
    "Unit Price",
    "Est Pkg Price",
    "Est Unit Price",
    "",
    "",
    "",
    "",
  ]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle className={classes.dialogTitleWithClose}>
        {isReturnScoped
          ? "Temporary NDCs for This Return"
          : "Temporary NDC Management"}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          className={classes.dialogCloseButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <CircularProgress />
        ) : displayData.length === 0 ? (
          <p>No temporary NDCs to review.</p>
        ) : (
          <TableOrganism
            header={header}
            rows={createTemporaryNdcRows(displayData, {
              setOpenEdit: setOpenEditDialog,
              setSelected: setSelectedTempDrug,
              acceptMutate: acceptMutation.mutate,
              replaceMutate: replaceMutation.mutate,
              requestDelete: setConfirmDeleteId,
              loadingAction,
            })}
          />
        )}
      </DialogContent>
      {openEditDialog && selectedTempDrug && (
        <EditTemporaryNdcModal
          open={openEditDialog}
          drug={selectedTempDrug}
          onClose={() => setOpenEditDialog(false)}
        />
      )}
      <AreYouSure
        open={confirmDeleteId != null}
        onClose={() => setConfirmDeleteId(null)}
        toBeDeletedObject={confirmDeleteId}
        deleteObject={(id: number) => deleteMutation.mutate(id)}
      />
    </Dialog>
  );
};

export default TemporaryNdcModal;
